<script setup>
import {useStore} from "vuex";
import HelpVideoModal from "./HelpVideoModal";
import useUser from "@/composables/useUser";
import {computed, ref} from "vue";
import AiAssignModal from "@/components/sitemap/modals/AiAssignModal.vue";
import useSitemap from "@/composables/useSitemap";

const {hasSitemapAiAutoAssignFeature} = useUser();

const {updateAutoAssignStatusMutation} = useSitemap();
const {mutate: updateAutoAssignStatus} = updateAutoAssignStatusMutation;

const showExportOptions = ref(false);
const showHelpVideo = ref(false);
const showAiAssignModal = ref(false);

const store = useStore();
const onboardingStep = computed(() => store.getters['strategy/onboardingStep']);
const showHelpVideosAndTours = computed(() => store.getters['whitelabel/showHelpVideosAndTours']);

const hasKeywordsAssigned = computed(() => {
  const sitemap = store.getters['strategy/sitemap'];

  return sitemap.some(sitemapPage => sitemapPage.keywords?.length > 0)
});

const handleAutoAssign = () => {
  if (hasKeywordsAssigned.value > 0) {
    showAiAssignModal.value = true;
    return
  }

  updateAutoAssignStatus();
}

</script>

<template>
  <div class="sitemap_heading">
    <div class="title">
      <h3 v-if="onboardingStep === 'COMPLETED'">
        Map your keywords to pages
      </h3>
      <h3 v-else>
        3. Map your keywords to pages
      </h3>
      <button
        v-if="showHelpVideosAndTours"
        class="btn btn_md primary_btn watch-video"
        @click="showHelpVideo = true"
      >
        <i class="video_icon" />
        <span>Watch help video</span>
      </button>
    </div>
    <div class="desc">
      <div v-if="onboardingStep === 'COMPLETED'">
        <p>
          Map each keyword you've selected to the page you want to rank in Google.
          Selecting the page on your website that is most closely aligned to the keyword is key for performance.
        </p>
        <p>Tip - use the auto-assign feature and the platform will map keywords you already rank.</p>
      </div>
      <div v-else>
        <p>
          Map each keyword you've selected to the page you want to rank in Google.
          It's important you select the page on your website that is most closely-aligned to the keyword.
        </p>
      </div>
      <div class="action_buttons">
        <button
          class="auto_assign"
          @click="store.dispatch('strategy/tryAutoAssignPagesAndKeywords')"
        >
          <svg-vue icon="icons/star" />
          Auto-Assign
        </button>

        <button
          v-if="hasSitemapAiAutoAssignFeature"
          class="auto_assign"
          @click="handleAutoAssign"
        >
          <svg-vue icon="icons/sparkles-outline" />
          AI Assign
        </button>

        <div
          v-if="onboardingStep === 'COMPLETED'"
          class="export"
          @click="showExportOptions = !showExportOptions"
        >
          <svg-vue icon="icons/download-simple" />
          Export
          <div
            v-if="showExportOptions"
            class="export_options"
            @mouseleave="showExportOptions = false"
          >
            <a
              href="/export/pdf/sitemap"
              target="_blank"
            >
              Export as PDF
            </a>
            <a
              href="/export/csv/sitemap"
              target="_blank"
            >
              Export as CSV
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
  <help-video-modal v-if="showHelpVideo" />
  <AiAssignModal
    v-if="showAiAssignModal"
    @close="() => showAiAssignModal = false"
  />
</template>

<style lang="scss" scoped>
.sitemap_heading {
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      font-weight: 700;
      line-height: 1.25;
      letter-spacing: -0.042em;
      color: var(--primary-font-color);
      font-size: 2.5rem;
    }

    .watch-video {
      margin: 0;
      height: 42px;
      display: inline-flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;

      span {
        font-weight: 400;
        line-height: 1.277;
        text-align: center;
        letter-spacing: -0.5px;
        text-transform: capitalize;
        font-size: 16px;
        margin: 0;
        padding: 0;
        outline: none;
        box-sizing: border-box;
        color: var(--white);
        margin-left: 10px;
        white-space: nowrap;
      }

      .video_icon {
        width: 23px;
        height: 21px;
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        background-image: url("data:image/svg+xml,%3Csvg width='23' height='21' viewBox='0 0 23 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.94745 5.1875H13.4738C14.1717 5.1875 14.8411 5.45089 15.3346 5.91973C15.8281 6.38857 16.1053 7.02446 16.1053 7.6875V15.1875C16.1053 15.3533 16.036 15.5122 15.9126 15.6294C15.7893 15.7467 15.6219 15.8125 15.4474 15.8125H4.92113C4.22319 15.8125 3.55384 15.5491 3.06032 15.0803C2.56681 14.6114 2.28955 13.9755 2.28955 13.3125V5.8125C2.28955 5.64674 2.35886 5.48777 2.48224 5.37056C2.60562 5.25335 2.77296 5.1875 2.94745 5.1875V5.1875Z' stroke='white' stroke-width='1.6875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M16.105 9.25L20.7102 6.75V14.25L16.105 11.75' stroke='white' stroke-width='1.6875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      }
    }
  }

  .desc {
    display: flex;
    justify-content: space-between;

    p {
      max-width: 95%;
      margin-bottom: 5px;
      font-size: 1rem;
      line-height: 1.625;
      letter-spacing: -0.01em;
      color: var(--secondary-font-color);
    }

    .action_buttons {
      display: flex;
      gap: 16px;
      height: 40px;
      font-size: 16px;
      font-weight: 400;
      line-height: 1.277;
      text-align: center;
      text-transform: capitalize;

      .auto_assign {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 15px;
        gap: 10px;

        width: 160px;

        background-color: #FFD673;
        border-radius: 12px;

        font-weight: 700;
        color: #040707;

        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;
        transition: background-color .3s;

        svg {
          height: 16px;
          width: 16px;

          :deep(path) {
            stroke: #040707;
            stroke-width: 1;
          }
        }

        &:hover {
          background-color: #EAB64D;
        }

      }

      .export {
        cursor: pointer;
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 15px;
        gap: 10px;

        width: 110px;

        background: #FFFFFF;
        border: 1px solid var(--primary-color);
        border-radius: 12px;

        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;

        color: var(--primary-color);

        &:hover {
          border-color: var(--primary-color-dark);
          color: var(--primary-color-dark);

          svg {
            :deep(path) {
              stroke: var(--primary-color-dark);
            }
          }
        }

        svg {
          height: 16px;
          width: 16px;

          :deep(path) {
            stroke: var(--primary-color);
          }
        }

        .export_options {
          position: absolute;
          top: 50px;
          right: 0;
          background: #FFFFFF;
          border: 1px solid #F0E9FF;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          white-space: nowrap;
          z-index: 10;

          a {
            color: var(--primary-font-color);
            padding: 8px 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            font-weight: 400;
            line-height: 16px;
            letter-spacing: -0.1px;
            text-align: left;
            cursor: pointer;

            &:hover {
              background: #F0E9FF;
              border-radius: 8px;
            }
          }
        }

      }
    }
  }
}
</style>
